'use client';

import { useEffect, useState } from 'react';
import { getCourseDetail, getConsentStatus } from '@/api';
import CoursePlayer from '@/app/components/CoursePlayer/CoursePlayer';
import ConsentModal from '@/app/components/ConsentModal/ConsentModal';

const normalizeCoursePayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.course && typeof payload.course === "object") return payload.course;
  if (payload.data && typeof payload.data === "object") return payload.data;
  return payload;
};

const CoursePageClient = ({ courseId }) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consented, setConsented] = useState(true);
  const [checkingConsent, setCheckingConsent] = useState(true);

  useEffect(() => {
    if (!courseId) return;

    let cancelled = false;

    const fetchCourseAndConsent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch course details and check consent status in parallel
        const [coursePayload, consentStatus] = await Promise.all([
          getCourseDetail(courseId),
          getConsentStatus().catch((err) => {
            console.error('Failed to retrieve consent status:', err);
            // Default to consented = true if there is an auth error, so the page handles normal redirect/login
            return { consented: true };
          })
        ]);

        const courseData = normalizeCoursePayload(coursePayload);
        if (!cancelled) {
          setCourse(courseData);
          setConsented(consentStatus?.consented ?? true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setCheckingConsent(false);
        }
      }
    };

    fetchCourseAndConsent();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  if (loading || checkingConsent) {
    return (
      <div className="flex flex-col justify-center items-center space-y-2 py-20 min-h-[50vh]">
        <div className="w-10 h-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
        <p className="text-gray-600">Loading course player...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600">
        <p>Error loading course: {error?.message || 'An unexpected error occurred.'}</p>
      </div>
    );
  }

  if (!course) {
    return <p className="p-8 text-center">Course not found.</p>;
  }

  return (
    <section className="bg-[#def1ff] min-h-screen relative">
      <CoursePlayer course={course} />
      {!consented && (
        <ConsentModal onAccept={() => setConsented(true)} />
      )}
    </section>
  );
};

export default CoursePageClient;

